import React, { useState } from "react";
import { Paperclip, Download, Loader2 } from "lucide-react";
import { callForHelpService } from "@/features/call-for-help/services/call-for-help.services";
import { showToast } from "@/lib/utils/toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const formatFileSize = (bytes) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0,
    size = bytes;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const AttachmentList = ({ attachments = [] }) => {
  const [downloadingId, setDownloadingId] = useState(null);

  if (!attachments.length) {
    return (
      <div
        className="flex items-center justify-center rounded-lg border
                   border-dashed p-4
                   border-gray-200 dark:border-gray-700"
      >
        <p className="text-xs text-muted-foreground dark:text-gray-500">
          No attachments
        </p>
      </div>
    );
  }

  const handleDownload = async (attachment) => {
    const fileId = attachment.fileId;
    if (!fileId) return;

    setDownloadingId(fileId);
    try {
      await callForHelpService.downloadAttachmentForFileId(attachment);
    } catch (err) {
      showToast.error("Failed to download file");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="space-y-1.5">
        {/* ── Count ── */}
        <p className="text-xs text-muted-foreground dark:text-gray-400">
          {attachments.length} file(s) attached
        </p>

        {/* ── File List ── */}
        <ul
          className="space-y-1.5 max-h-52 overflow-y-auto
                     scrollbar-thin
                     scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600"
        >
          {attachments.map((attachment, idx) => {
            const fileId = attachment.fileId ?? idx;
            const isDownloading = downloadingId === fileId;

            return (
              <li
                key={fileId ?? idx}
                className="flex items-center justify-between gap-2
                           px-2 py-1.5 rounded-md border transition-colors
                           bg-gray-50   border-gray-200  hover:bg-gray-100
                           dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-750"
              >
                {/* File Info */}
                <div className="flex items-center gap-1.5 min-w-0">
                  <Paperclip
                    className="w-3.5 h-3.5 shrink-0
                               text-gray-400 dark:text-gray-500"
                  />
                  <div className="min-w-0">
                    {/* Filename + Tooltip */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p
                          className="truncate text-xs font-medium cursor-default
                                     text-gray-700 dark:text-gray-200"
                        >
                          {attachment.name}
                        </p>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        align="start"
                        className="max-w-xs break-all text-xs"
                      >
                        {attachment.name}
                      </TooltipContent>
                    </Tooltip>

                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {formatFileSize(attachment.size)}
                    </p>
                  </div>
                </div>

                {/* Download button */}
                <button
                  type="button"
                  onClick={() => handleDownload(attachment)}
                  disabled={isDownloading}
                  title="Download"
                  className="shrink-0 transition-colors disabled:opacity-40
                             text-gray-400 hover:text-[#2d3a8c]
                             dark:text-gray-500 dark:hover:text-[#3d4fba]"
                >
                  {isDownloading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Download className="w-3.5 h-3.5" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </TooltipProvider>
  );
};

export default AttachmentList;
